/**
 * KOA Project - Main Application Entry Point
 * Modular JavaScript architecture with ES6 modules
 * Initializes all components and manages application state
 */

import { koaModal } from './components/modal.js';
import { musicPlayer } from './components/musicPlayer.js';

/**
 * KOA Application Class
 * Main application controller that manages all components
 */
class KoaApp {
  /**
   * Initialize the KOA application
   * Sets up all components and application state
   */
  constructor() {
    this.components = {
      modal: koaModal,
      musicPlayer: musicPlayer,
    };

    this.isInitialized = false;
    this.isSafari = this.detectSafari();
    this.init();
  }

  /**
   * Detect if browser is Safari
   * @returns {boolean} True if Safari browser
   */
  detectSafari() {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.includes('safari') &&
      !userAgent.includes('chrome') &&
      !userAgent.includes('chromium')
    );
  }

  /**
   * Initialize application
   * Sets up DOM ready state and component initialization
   */
  init() {
    // Safari-specific handling for page refresh/back-forward cache
    if (this.isSafari) {
      // Force remove loaded class on page show (handles refresh and back/forward)
      window.addEventListener('pageshow', event => {
        console.log(
          'KoaApp: Safari pageshow event detected, persisted:',
          event.persisted
        );
        // Remove loaded class to ensure fade-in works on refresh
        document.body.classList.remove('loaded');

        // Small delay to ensure class removal is processed
        setTimeout(() => {
          this.handleFadeIn();
        }, 50);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeComponents();
      });
    } else {
      this.initializeComponents();
    }
  }

  /**
   * Handle fade-in effect with Safari-specific logic
   */
  handleFadeIn() {
    console.log('KoaApp: Adding fade-in effect to body...');

    // Force removal of loaded class first (especially important for Safari)
    document.body.classList.remove('loaded');

    // Force a reflow to ensure the class removal is processed
    document.body.offsetHeight;

    // Add fade-in effect with appropriate delay
    const delay = this.isSafari ? 150 : 100;
    setTimeout(() => {
      document.body.classList.add('loaded');
      console.log('KoaApp: Fade-in effect applied');
    }, delay);
  }

  /**
   * Initialize all application components
   * Sets up each component in the correct order
   */
  initializeComponents() {
    try {
      console.log('KoaApp: Starting component initialization...');

      // Initialize modal component
      if (
        this.components.koaModal &&
        typeof this.components.koaModal.init === 'function'
      ) {
        console.log('KoaApp: Initializing modal component...');
        this.components.koaModal.init();
      }

      // Initialize music player component with explicit timing
      if (
        this.components.musicPlayer &&
        typeof this.components.musicPlayer.init === 'function'
      ) {
        console.log('KoaApp: Initializing music player component...');
        // Force re-initialization to ensure fresh state after page refresh
        this.components.musicPlayer.init();
      }

      this.setupGlobalErrorHandling();
      this.setupPerformanceMonitoring();

      this.isInitialized = true;

      // Handle fade-in with Safari-specific logic
      this.handleFadeIn();

      // Dispatch custom event for other scripts that might need to know when app is ready
      this.dispatchAppReadyEvent();

      console.log('KoaApp: All components initialized successfully');
    } catch (error) {
      console.error('KoaApp initialization error:', error);
    }
  }

  /**
   * Set up global error handling
   * Catches and logs unhandled errors
   */
  setupGlobalErrorHandling() {
    window.addEventListener('error', event => {});

    window.addEventListener('unhandledrejection', event => {});
  }

  /**
   * Set up basic performance monitoring
   * Logs performance metrics for debugging
   */
  setupPerformanceMonitoring() {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = performance.getEntriesByType('navigation')[0];
          if (perfData) {
            console.log('Performance metrics:', {
              domContentLoaded: Math.round(
                perfData.domContentLoadedEventEnd -
                  perfData.domContentLoadedEventStart
              ),
              loadComplete: Math.round(
                perfData.loadEventEnd - perfData.loadEventStart
              ),
              totalTime: Math.round(
                perfData.loadEventEnd - perfData.fetchStart
              ),
            });
          }
        }, 0);
      });
    }
  }

  /**
   * Dispatch application ready event
   * Allows other scripts to listen for app initialization
   */
  dispatchAppReadyEvent() {
    const event = new CustomEvent('koaAppReady', {
      detail: {
        app: this,
        components: this.components,
        timestamp: Date.now(),
      },
    });

    document.dispatchEvent(event);
  }

  /**
   * Get component instance by name
   * @param {string} componentName - Name of the component
   * @returns {Object|null} Component instance or null if not found
   */
  getComponent(componentName) {
    return this.components[componentName] || null;
  }

  /**
   * Check if application is fully initialized
   * @returns {boolean} True if app is initialized
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Get application state information
   * @returns {Object} Object containing app state
   */
  getState() {
    return {
      isInitialized: this.isInitialized,
      components: Object.keys(this.components),
      timestamp: Date.now(),
    };
  }
}

// Initialize the application
const koaApp = new KoaApp();

// Make app instance available globally for debugging
if (typeof window !== 'undefined') {
  window.koaApp = koaApp;
}

// Export for potential use in other modules
export default KoaApp;
export { koaApp };
